import React, { useCallback, useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyStatePanel } from '@/components/ui/empty-state-panel';
import { useAppToast } from '@/hooks/useAppToast';
import {
  getStudentPackageGrantChain,
  grantStudentPackageAccess,
  revokeAccessGrant,
  type GrantChainItem,
} from '@/services/admin/student.service';

type Props = {
  studentId: string;
  packageId: string;
};

const sourceLabel = (source: string) => {
  switch (source) {
    case 'Payment':
      return 'Pagamento';
    case 'Subscription':
      return 'Assinatura';
    case 'Voucher':
      return 'Voucher';
    case 'Admin':
      return 'Admin';
    case 'Trial':
      return 'Trial';
    default:
      return source;
  }
};

const statusVariant = (status: string): 'default' | 'secondary' | 'outline' | 'destructive' => {
  if (status === 'Revoked') return 'destructive';
  if (status === 'Active') return 'secondary';
  if (status === 'Scheduled') return 'outline';
  return 'secondary';
};

export const StudentPackageAccessSection: React.FC<Props> = ({ studentId, packageId }) => {
  const toast = useAppToast();
  const [isLoading, setIsLoading] = useState(true);
  const [accessUntil, setAccessUntil] = useState<string | null>(null);
  const [nextBillingAt, setNextBillingAt] = useState<string | null>(null);
  const [grants, setGrants] = useState<GrantChainItem[]>([]);
  const [durationDays, setDurationDays] = useState('30');
  const [isGranting, setIsGranting] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<GrantChainItem | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [isRevoking, setIsRevoking] = useState(false);

  const loadChain = useCallback(async () => {
    setIsLoading(true);
    try {
      const chain = await getStudentPackageGrantChain(studentId, packageId);
      setAccessUntil(chain.accessUntil ?? null);
      setNextBillingAt(chain.nextBillingAt ?? null);
      setGrants(chain.grants ?? []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao carregar acessos');
      setGrants([]);
    } finally {
      setIsLoading(false);
    }
  }, [studentId, packageId, toast]);

  useEffect(() => {
    void loadChain();
  }, [loadChain]);

  const handleGrant = async () => {
    const days = Number(durationDays);
    if (!Number.isFinite(days) || days < 1) {
      toast.error('Informe uma duração válida em dias');
      return;
    }

    setIsGranting(true);
    try {
      const result = await grantStudentPackageAccess(studentId, packageId, days);
      toast.success(result.message ?? 'Acesso concedido');
      await loadChain();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao conceder acesso');
    } finally {
      setIsGranting(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget || !revokeReason.trim()) return;
    setIsRevoking(true);
    try {
      await revokeAccessGrant(revokeTarget.id, revokeReason.trim());
      toast.success('Acesso revogado');
      setRevokeTarget(null);
      setRevokeReason('');
      await loadChain();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao revogar acesso');
    } finally {
      setIsRevoking(false);
    }
  };

  const formatDate = (value: string | null) =>
    value ? new Date(value).toLocaleString('pt-BR') : '—';

  return (
    <section className="bg-white rounded-lg shadow-sm border p-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Acessos</h2>
        <p className="text-sm text-muted-foreground">Cadeia de tempo de acesso ao pacote</p>
      </div>

      {isLoading ? (
        <div className="space-y-3 animate-pulse" aria-busy="true">
          <div className="h-4 bg-gray-200 rounded w-1/3" />
          <div className="h-10 bg-gray-200 rounded" />
          <div className="h-10 bg-gray-200 rounded" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Acesso até</p>
              <p className="font-medium">{formatDate(accessUntil)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Próxima cobrança</p>
              <p className="font-medium">{formatDate(nextBillingAt)}</p>
            </div>
          </div>

          {grants.length === 0 ? (
            <EmptyStatePanel>
              Nenhum grant ainda — conceda acesso ou aguarde uma compra.
            </EmptyStatePanel>
          ) : (
            <ul className="space-y-3">
              {grants.map((grant) => (
                <li
                  key={grant.id}
                  className="rounded-lg border bg-white p-4 shadow flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                >
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline">{sourceLabel(grant.source)}</Badge>
                      <Badge variant={statusVariant(grant.status)}>{grant.status}</Badge>
                      <span className="text-sm text-muted-foreground">
                        {grant.validUntil == null ? 'em aberto' : `${grant.durationDays} dias`}
                      </span>
                    </div>
                    <p className="text-sm">
                      {formatDate(grant.validFrom)} → {formatDate(grant.validUntil)}
                    </p>
                    {grant.note && <p className="text-xs text-muted-foreground">{grant.note}</p>}
                  </div>
                  {grant.status !== 'Revoked' && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setRevokeTarget(grant)}
                    >
                      Revogar
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="rounded-lg border p-4 space-y-3">
            <p className="text-sm font-medium">Conceder acesso</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                type="number"
                min={1}
                value={durationDays}
                onChange={(e) => setDurationDays(e.target.value)}
                placeholder="Dias"
                className="sm:max-w-[140px]"
              />
              <Button onClick={handleGrant} disabled={isGranting}>
                {isGranting ? 'Concedendo...' : 'Confirmar'}
              </Button>
            </div>
          </div>
        </>
      )}

      <Dialog open={!!revokeTarget} onOpenChange={(open) => !open && setRevokeTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revogar acesso</DialogTitle>
            <DialogDescription>
              Esta ação remove o período deste grant. Informe o motivo.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value)}
            placeholder="Motivo da revogação"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevoke}
              disabled={isRevoking || !revokeReason.trim()}
            >
              {isRevoking ? 'Revogando...' : 'Revogar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};
